import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { superFreteApi } from "@/services";
import { useTenant } from "./useTenant";
import { useAllIntegrationsStatus } from "./useIntegrationsStatus";

interface SuperFreteStatus {
  connected: boolean;
  user_info?: Record<string, unknown>;
}

interface ConnectParams {
  api_token: string;
}

export function useSuperFreteStatus() {
  const { data: integrations, isLoading } = useAllIntegrationsStatus();

  const data = useMemo((): SuperFreteStatus => {
    const integration = integrations?.find(
      (i) => i.provider === "super_frete" && i.is_active
    );
    if (!integration) return { connected: false };
    const metadata = integration.metadata as Record<string, unknown> | null;
    return {
      connected: true,
      user_info: (metadata?.user_info as Record<string, unknown>) || undefined,
    };
  }, [integrations]);

  return { data, isLoading };
}

export function useConnectSuperFrete() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (params: ConnectParams) => {
      return await superFreteApi.connect(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
    },
  });
}

export function useDisconnectSuperFrete() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async () => {
      await superFreteApi.disconnect();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
    },
  });
}

export function useSuperFreteCalculateShipping() {
  return useMutation({
    mutationFn: async (params: {
      cepOrigem: string;
      cepDestino: string;
      peso?: number;
      comprimento?: number;
      largura?: number;
      altura?: number;
    }) => {
      const data = await superFreteApi.calculateShipping(params);
      return data as any[];
    },
  });
}

export function useSuperFreteCreateShipment() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (params: Record<string, unknown>) => {
      return await superFreteApi.createShipment(params);
    },
    onSuccess: (_, variables) => {
      const saleId = variables.sale_id as string;
      queryClient.invalidateQueries({ queryKey: ["shipments", saleId, tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["sales", tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-metrics", tenant?.id] });
    },
  });
}

export function useSuperFreteCancel() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (shipmentId: string) => {
      return await superFreteApi.cancel(shipmentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shipments"] });
      queryClient.invalidateQueries({ queryKey: ["sales", tenant?.id] });
    },
  });
}

export function useSuperFreteRetryPrint() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (shipmentId: string) => {
      return await superFreteApi.retryPrint(shipmentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shipments", tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["sales", tenant?.id] });
    },
  });
}

export function useSuperFreteFetchLabel() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (shipmentId: string) => {
      return await superFreteApi.fetchLabel(shipmentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shipments", tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["sales", tenant?.id] });
    },
  });
}
